import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Settings, Smartphone, Wrench, MapPin, Users, Loader2, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRACKING_FIELDS = [
  { key: 'house_proximity_meters', label: 'House Proximity (m)', desc: 'Distance at which a worker is considered "at" a house' },
  { key: 'house_proximity_exit_meters', label: 'House Proximity Exit (m)', desc: 'Distance at which the worker leaves a house zone' },
  { key: 'max_mark_house_radius_meters', label: 'Max Mark House Radius (m)', desc: 'Max distance to mark a house status (Non Deliverable / Gated)' },
  { key: 'stop_duration_seconds', label: 'Stop Duration (s)', desc: 'Seconds stationary before counting as a stop' },
  { key: 'movement_threshold_meters', label: 'Movement Threshold (m)', desc: 'Minimum movement to register a new GPS point' },
  { key: 'manual_duplicate_radius_meters', label: 'Manual Duplicate Radius (m)', desc: 'Min distance between manual delivery markers' },
  { key: 'max_route_point_accuracy_meters', label: 'Max Route Point Accuracy (m)', desc: 'GPS points less accurate than this are discarded' },
  { key: 'route_coord_precision_decimals', label: 'Route Coord Precision', desc: 'Decimal places stored per coordinate' },
  { key: 'auto_save_interval_ms', label: 'Auto Save Interval (ms)', desc: 'How often the app syncs tracking state to the server' },
  { key: 'min_meaningful_deliveries', label: 'Min Meaningful Deliveries', desc: 'Resume-modal threshold (deliveries)' },
  { key: 'min_meaningful_duration', label: 'Min Meaningful Duration (s)', desc: 'Resume-modal threshold (seconds)' },
  { key: 'session_max_age_hours', label: 'Session Max Age (hours)', desc: 'Leave empty for infinite (multi-day jobs)', nullable: true },
  { key: 'hourly_rate', label: 'Hourly Rate ($)', desc: 'Default hourly rate used by the app' },
];

export default function AdminAppConfig() {
  const { getAuthHeader } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/app-config`);
        setConfig(res.data);
      } catch (e) {
        toast.error('Failed to load app config');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (key, value) => setConfig((c) => ({ ...c, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...config };
      delete payload.updated_at;
      delete payload.updated_by;
      if (payload.session_max_age_hours === '' ) payload.session_max_age_hours = null;
      if (typeof payload.team_route_colors === 'string') {
        payload.team_route_colors = payload.team_route_colors.split(',').map((s) => s.trim()).filter(Boolean);
      }
      const res = await axios.put(`${API}/admin/app-config`, payload, getAuthHeader());
      setConfig(res.data);
      toast.success('App config saved — live for the app immediately');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save app config');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-orange-500" size={32} />
      </div>
    );
  }
  if (!config) return <div className="p-6 text-gray-500">Could not load config.</div>;

  const colorsValue = Array.isArray(config.team_route_colors)
    ? config.team_route_colors.join(', ')
    : (config.team_route_colors || '');

  return (
    <div className="p-6 space-y-6" data-testid="admin-app-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="text-orange-500" size={24} /> App Config
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Runtime settings the mobile app fetches at startup (GET /api/app-config).
            {config.updated_at && ` Last updated ${new Date(config.updated_at).toLocaleString()} by ${config.updated_by || 'admin'}.`}
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="app-config-save-btn" className="bg-orange-500 hover:bg-orange-600">
          {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />}
          Save Config
        </Button>
      </div>

      {/* App versions — per platform */}
      <Card className="p-5 space-y-4">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <Smartphone size={18} className="text-blue-500" /> App Versions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3 border rounded-lg p-4">
            <p className="text-sm font-semibold text-gray-800">iOS</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cfg-ios-version" className="text-xs">Version</Label>
                <Input id="cfg-ios-version" data-testid="app-config-ios-version-input" value={config.ios_app_version || ''}
                  onChange={(e) => setField('ios_app_version', e.target.value)} placeholder="1.0.1" />
              </div>
              <div>
                <Label htmlFor="cfg-ios-build" className="text-xs">Build</Label>
                <Input id="cfg-ios-build" data-testid="app-config-ios-build-input" type="number" value={config.ios_app_build ?? ''}
                  onChange={(e) => setField('ios_app_build', e.target.value === '' ? 0 : parseInt(e.target.value, 10))} placeholder="11" />
              </div>
            </div>
            <div>
              <Label htmlFor="cfg-ios-store" className="text-xs">Store / TestFlight URL</Label>
              <Input id="cfg-ios-store" data-testid="app-config-ios-store-input" value={config.ios_store_url || ''}
                onChange={(e) => setField('ios_store_url', e.target.value)} placeholder="https://testflight.apple.com/join/..." />
            </div>
          </div>
          <div className="space-y-3 border rounded-lg p-4">
            <p className="text-sm font-semibold text-gray-800">Android</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cfg-android-version" className="text-xs">Version</Label>
                <Input id="cfg-android-version" data-testid="app-config-android-version-input" value={config.android_app_version || ''}
                  onChange={(e) => setField('android_app_version', e.target.value)} placeholder="1.0.2" />
              </div>
              <div>
                <Label htmlFor="cfg-android-build" className="text-xs">Build</Label>
                <Input id="cfg-android-build" data-testid="app-config-android-build-input" type="number" value={config.android_app_build ?? ''}
                  onChange={(e) => setField('android_app_build', e.target.value === '' ? 0 : parseInt(e.target.value, 10))} placeholder="14" />
              </div>
            </div>
            <div>
              <Label htmlFor="cfg-android-store" className="text-xs">Play Store URL</Label>
              <Input id="cfg-android-store" data-testid="app-config-android-store-input" value={config.android_store_url || ''}
                onChange={(e) => setField('android_store_url', e.target.value)} placeholder="https://play.google.com/store/apps/details?id=..." />
            </div>
          </div>
        </div>
      </Card>

      {/* Maintenance mode */}
      <Card className={`p-5 space-y-4 ${config.maintenance_mode ? 'border-red-300 bg-red-50' : ''}`}>
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Wrench size={18} className={config.maintenance_mode ? 'text-red-600' : 'text-gray-500'} /> Maintenance Mode
          </h2>
          <Switch
            checked={!!config.maintenance_mode}
            onCheckedChange={(v) => setField('maintenance_mode', v)}
            data-testid="app-config-maintenance-toggle"
          />
        </div>
        <p className="text-xs text-gray-500">
          When ON, the app shows a blocking maintenance screen. The server keeps accepting worker
          syncs so nobody mid-route loses data. The admin portal stays usable.
        </p>
        <div>
          <Label htmlFor="cfg-maint-msg">Maintenance Message (shown in the app)</Label>
          <Textarea id="cfg-maint-msg" data-testid="app-config-maintenance-message" rows={2}
            value={config.maintenance_message || ''}
            onChange={(e) => setField('maintenance_message', e.target.value)}
            placeholder="We're doing a quick upgrade — back shortly." />
        </div>
      </Card>

      {/* Tracking settings */}
      <Card className="p-5 space-y-4">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <MapPin size={18} className="text-green-600" /> Delivery Tracking
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TRACKING_FIELDS.map((f) => (
            <div key={f.key}>
              <Label htmlFor={`cfg-${f.key}`} className="text-xs">{f.label}</Label>
              <Input
                id={`cfg-${f.key}`}
                data-testid={`app-config-${f.key.replaceAll('_', '-')}-input`}
                type="number"
                step="any"
                value={config[f.key] ?? ''}
                onChange={(e) => setField(f.key, e.target.value === '' ? (f.nullable ? null : 0) : Number(e.target.value))}
                placeholder={f.nullable ? 'infinite' : ''}
              />
              <p className="text-[11px] text-gray-400 mt-0.5">{f.desc}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Team settings */}
      <Card className="p-5 space-y-4">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <Users size={18} className="text-purple-500" /> Team Jobs
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cfg-team-sync">Team Sync Interval (ms)</Label>
            <Input id="cfg-team-sync" data-testid="app-config-team-sync-input" type="number"
              value={config.team_sync_interval_ms ?? ''}
              onChange={(e) => setField('team_sync_interval_ms', e.target.value === '' ? 0 : parseInt(e.target.value, 10))} />
            <p className="text-[11px] text-gray-400 mt-0.5">How often the app polls team progress</p>
          </div>
          <div>
            <Label htmlFor="cfg-team-colors">Team Route Colors (comma-separated hex)</Label>
            <Input id="cfg-team-colors" data-testid="app-config-team-colors-input"
              value={colorsValue}
              onChange={(e) => setField('team_route_colors', e.target.value)} />
            <div className="flex gap-1 mt-1.5">
              {colorsValue.split(',').map((c) => c.trim()).filter(Boolean).map((c, i) => (
                <span key={i} className="w-5 h-5 rounded-full border border-gray-200" style={{ backgroundColor: c }} title={c}></span>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
